/**
 * Created by Jerker Nord on 2022-10-28.
 */
import {gsap} from "gsap";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {ISymbol, LonerSymbolStates, SimpleReelModelState, SYMBOL_NAMES} from "../../../core/reel/ReelInterfaces";
import {LonerStatics} from "../../LonerStatics";
import {SymbolUtils} from "../../utils/SymbolUtils";
import {LonerSymbol} from "../../reels/symbols/main/LonerSymbol";
import {LonerScatterSymbol} from "../../reels/symbols/main/LonerScatterSymbol";
import {Loner} from "../../Loner";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {LonerBigWildPresentationSymbol2x2} from "../../reels/symbols/main/bigsymbols/LonerBigWildPresentationSymbol2x2";
import {LonerBigWildPresentationSymbol3x3} from "../../reels/symbols/main/bigsymbols/LonerBigWildPresentationSymbol3x3";

export class LonerMainReelView extends PIXI.Container {

    public animatedStepData:any = {
        y : 0
    }
    public animatedStepDataStart:any[];
    private _reelId:number;
    private _isRestore:boolean;
    private _reelSymbols:ISymbol[] = [];
    private _reelSymbolsContainer:PIXI.Container;
    private _stackedSymbolsContainer:PIXI.Container;
    private _stopSymbolNames:string[];
    private _stopping:boolean = false;
    private _spinning:boolean = false;
    private _tryToStop:boolean;
    //Makes sure the big symbol has time to move down before we do result reels
    private _bigSymbolCounter:number = 0;
    private _masterSpinTimeline:gsap.core.Timeline;
    private _masterSpinTimelineFast:gsap.core.Timeline;
    private _stateChangeCallback:Function;
    private _darkOverlay:PIXI.Sprite;
    private _spinStartOvershootBackTweenFast:gsap.core.Tween;
    private _startSpinNormalTL:gsap.core.Timeline;
    private _startSpinFastTL:gsap.core.Timeline;
    private _spinTL:gsap.core.Timeline;
    private _spinTLFast:gsap.core.Timeline;
    private _stopBounceTL:gsap.core.Timeline;
    private _stopBounceFastTL:gsap.core.Timeline;
    private _stopAdjustTL:gsap.core.Timeline;
    private _stopAdjustFastTL:gsap.core.Timeline;
    private _stopSteps:number;
    private _addSymbol:ISymbol;
    private _allowedSymbolCounter:number = 0;
    private _activateDarkReelTL:gsap.core.Timeline;
    private _deActivateDarkReelTL:gsap.core.Timeline;
    private _isFakeSpin:boolean = false;
    private _doInit:boolean = true;
    //If a big wild, all symbols under it should be set to not visible
    private _addBelowCounter:number = 0;
    private _bigSymbolOnReel:string = "";

    constructor(id:number, stateChangeCallback:Function) {
        super();
        this.name = "Reel " + id;
        this._reelId = id;
        this.position = LonerStatics.reelPositions[id];
        this._isRestore = false;
        this._stateChangeCallback = stateChangeCallback;
        this._reelSymbolsContainer = new PIXI.Container();
        this.addChild(this._reelSymbolsContainer);
        this._stackedSymbolsContainer = new PIXI.Container();
        this._stackedSymbolsContainer.visible = false;
        this.addChild(this._stackedSymbolsContainer);
        this._darkOverlay = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._darkOverlay.tint = 0x000000;
        this._darkOverlay.alpha = 0;
        this._darkOverlay.width = LonerStatics.symbolWidth;
        this._darkOverlay.height = LonerStatics.symbolHeight * 3;
        this._darkOverlay.position.y = LonerStatics.symbolHeight * 3;
        this._darkOverlay.position.x -= LonerStatics.symbolWidth / 2;
        this._darkOverlay.position.y -= LonerStatics.symbolHeight / 2;
        this.addChild(this._darkOverlay);

        this.animatedStepDataStart = [];
        for(let i:number = 0; i < 9; i++) {
            this.animatedStepDataStart.push({
                y : 0
            });
        }

        this.initTLs();
    }

    public init() {
        this._startSpinNormalTL = gsap.timeline({
            paused : true,
            onStart : () => {
                this._stateChangeCallback(SimpleReelModelState.SPIN);
                (<LonerSymbol>this._reelSymbols[2]).getNewStateAnimation(LonerSymbolStates.SPIN_IN);
            },
            onComplete : () => {
                this.startSpin();

                this.removeSym(<LonerSymbol>this._reelSymbols.pop());
                this.removeSym(<LonerSymbol>this._reelSymbols.pop());

                this.addSymbol();
                this.addSymbol();

                for(let i:number = 0; i < this._reelSymbols.length; i++) {
                    const symbol:ISymbol = this._reelSymbols[i];
                    (<LonerSymbol>symbol).reelPosition += 1;
                }

                this.animatedStepData.y = 0;

                this.updateSymbolsPosition(LonerStatics.spinSymbolHeight);
            }
        });
        this._startSpinNormalTL.add(() => {
            for(let i:number = 0; i < this._reelSymbols.length; i++) {
                if(this._reelSymbols[i].symbolName == SYMBOL_NAMES.S) {
                    (<LonerScatterSymbol>this._reelSymbols[i]).getNewStateAnimation(LonerSymbolStates.SPIN_OUT, LonerStatics.NORMAL_BLUR_DURATION_SPIN_START, LonerStatics.NORMAL_BLUR_DELAY_SPIN_START);
                } else {
                    (<LonerSymbol>this._reelSymbols[i]).getNewStateAnimation(LonerSymbolStates.SPIN_OUT, LonerStatics.NORMAL_BLUR_DURATION_SPIN_START, LonerStatics.NORMAL_BLUR_DELAY_SPIN_START);
                }
            }
        });
        this._startSpinNormalTL.add(this.getNormalStartOvershootTween());
        this._startSpinNormalTL.add(() => {
            for(let i:number = 0; i < this._reelSymbols.length; i++) {
                this.animatedStepDataStart[i].y = this._reelSymbols[i].position.y;
            }
        });
        const startBounceTweens:gsap.core.Tween[] = [];
        for(let i:number = 0; i < 9; i++) {
            startBounceTweens.push(this.getNormalSpinStartOvershootBackTween(i));
        }
        this._startSpinNormalTL.add(startBounceTweens);

        this._startSpinFastTL = gsap.timeline({
            paused : true,
            onStart : () => {
                this._stateChangeCallback(SimpleReelModelState.SPIN)
            },
            onComplete : () => {
                this.startSpin();

                this.removeSym(<LonerSymbol>this._reelSymbols.pop());
                this.removeSym(<LonerSymbol>this._reelSymbols.pop());

                this.addSymbol();
                this.addSymbol();

                for(let i:number = 0; i < this._reelSymbols.length; i++) {
                    const symbol:ISymbol = this._reelSymbols[i];
                    (<LonerSymbol>symbol).reelPosition += 1;
                }

                this.animatedStepData.y = 0;

                this.updateSymbolsPosition(LonerStatics.spinSymbolHeight);
            }
        });
        this._startSpinFastTL.add(this.getFastStartOvershootTween());
        this._startSpinFastTL.add(() => {
            for(let i:number = 0; i < this._reelSymbols.length; i++) {
                this.animatedStepDataStart[i].y = this._reelSymbols[i].position.y;
            }
        });
        const startFastBounceTweens:gsap.core.Tween[] = [];
        for(let i:number = 0; i < 9; i++) {
            startFastBounceTweens.push(this.getFastSpinStartOvershootBackTween(i));
        }
        this._startSpinFastTL.add(startFastBounceTweens);


        this._activateDarkReelTL = this.createActivateDarkReelTL();
        this._deActivateDarkReelTL = this.createDeActivateDarkReelTL();

        this._stopAdjustTL = gsap.timeline({
            paused : true,
            onComplete : () => {
                this.addSymbol(LonerSymbolStates.SPIN_IN);
                this.addSymbol(LonerSymbolStates.SPIN_IN);

                this.removeSym(<LonerSymbol>this._reelSymbols.pop());

                for(let i:number = 0; i < this._reelSymbols.length; i++) {
                    const symbol:ISymbol = this._reelSymbols[i];
                    (<LonerSymbol>symbol).reelPosition += 2;
                }
                this.animatedStepData.y = LonerStatics.STOP_SPIN_BOUNCE_PIXEL_AMOUNT;

                this.updateSymbolsPosition();

                Loner.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? this._stopBounceFastTL.restart() : this._stopBounceTL.restart();
            }
        });
        this._stopAdjustTL.add(() => {
            for(let i:number = 0; i < this._reelSymbols.length; i++) {
                this.animatedStepDataStart[i].y = this._reelSymbols[i].position.y;
            }
        });
        const stopBounceTweens:gsap.core.Tween[] = [];
        for(let i:number = 0; i < 9; i++) {
            stopBounceTweens.push(this.getNormalSpinStopTween(i));
        }
        this._stopAdjustTL.add(() => {
            for(let i:number = 0; i < this._reelSymbols.length; i++) {
                (<LonerSymbol>this._reelSymbols[i]).getNewStateAnimation(LonerSymbolStates.NORMAL, LonerStatics.BLUR_DELAY_SPIN_STOP, LonerStatics.BLUR_DURATION_SPIN_STOP);
            }
        })
        this._stopAdjustTL.add(stopBounceTweens);

        this._stopAdjustFastTL = gsap.timeline({
            paused : true,
            onComplete : () => {
                this.addSymbol(LonerSymbolStates.SPIN_IN);
                this.addSymbol(LonerSymbolStates.SPIN_IN);

                this.removeSym(<LonerSymbol>this._reelSymbols.pop());

                for(let i:number = 0; i < this._reelSymbols.length; i++) {
                    const symbol:ISymbol = this._reelSymbols[i];
                    (<LonerSymbol>symbol).reelPosition += 2;
                }

                this.animatedStepData.y = LonerStatics.STOP_SPIN_BOUNCE_PIXEL_AMOUNT;

                this.updateSymbolsPosition();

                Loner.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? this._stopBounceFastTL.restart() : this._stopBounceTL.restart();
            }
        });
        this._stopAdjustFastTL.add(() => {
            for(let i:number = 0; i < this._reelSymbols.length; i++) {
                this.animatedStepDataStart[i].y = this._reelSymbols[i].position.y;
            }
        });
        const stopBounceFastTweens:gsap.core.Tween[] = [];
        for(let i:number = 0; i < 9; i++) {
            stopBounceFastTweens.push(this.getFastSpinStopTween(i));
        }
        this._stopAdjustFastTL.add(() => {
            for(let i:number = 0; i < this._reelSymbols.length; i++) {
                (<LonerSymbol>this._reelSymbols[i]).getNewStateAnimation(LonerSymbolStates.NORMAL, LonerStatics.BLUR_DELAY_SPIN_STOP_FAST, LonerStatics.BLUR_DURATION_SPIN_STOP_FAST);
            }
        })
        this._stopAdjustFastTL.add(stopBounceFastTweens)

        this._stopBounceTL = gsap.timeline({
            paused : true,
            onComplete : () => {
                this._stateChangeCallback(SimpleReelModelState.STOPPED);
            },
            onStart : () => {
                this._stateChangeCallback(SimpleReelModelState.BOUNCE);
            }
        });
        this._stopBounceTL.add(this.getNormalSpinStopOvershootBackTween());

        this._stopBounceFastTL = gsap.timeline({
            paused : true,
            onComplete : () => {
                this._stateChangeCallback(SimpleReelModelState.STOPPED);
            },
            onStart : () => {
                this._stateChangeCallback(SimpleReelModelState.BOUNCE);
            }
        });
        this._stopBounceFastTL.add(this.getFastSpinStopOvershootBackTween());
    }

    public activateDarkReel():void {
        this._activateDarkReelTL.restart();
    }

    public start():gsap.core.Timeline {
        this._stopSteps = 8;
        this._stopSymbolNames = [];
        return Loner.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? this._startSpinFastTL : this._startSpinNormalTL;
    }

    //Used if a mainReels should be locked while the others spin
    public doFakeSpine():void {
        this._isFakeSpin = true;
        this._stateChangeCallback(SimpleReelModelState.SPIN);
    }

    public stop():void {
        this._tryToStop = true;
        if(this._isFakeSpin == true) {
            this._stateChangeCallback(SimpleReelModelState.READY_TO_STOP);
        }
    }

    public doStop(stopSymbolNames:string[]):void {
        this._stopSymbolNames = stopSymbolNames;
        this._spinning = false;
        this._tryToStop = false;
        this._stopping = true;
        this._stateChangeCallback(SimpleReelModelState.STOPPING);
        if(this._isFakeSpin == true) {
            this._isFakeSpin = false;
            this._stopping = false;
            this._stopSymbolNames = [];
            this._stateChangeCallback(SimpleReelModelState.STOPPED);
        }
    }

    //Removes old symbol and adds new one to stage
    public switchSymbol(newSymbol:LonerSymbol, symbolPosition:number):void {
        this.removeSym(<LonerSymbol>this._reelSymbols[symbolPosition]);
        this._reelSymbols[symbolPosition] = newSymbol;
        this._reelSymbolsContainer.addChildAt(newSymbol, (symbolPosition - 1));
        this.updateSymbolsPosition();
    }

    public moveSymbolToTopView(symbol:LonerSymbol):void {
        this._reelSymbolsContainer.setChildIndex(symbol, this._reelSymbolsContainer.children.length - 1);
    }

    public cleanUpReel():void {
        while(this._reelSymbols.length > 0) {
            this.removeSym(<LonerSymbol>this._reelSymbols.pop());
        }
    }

    public populateReel(symbols:string[]):void {
        let state:number;

        for(let i:number = 0; i < symbols.length; i++) {
            state = LonerSymbolStates.SPIN_IN;
            if(i > 1) {
                state = LonerSymbolStates.NORMAL;
            }
            if(symbols[i] == "S") {
                //Only transform TVs in the visible area
                if(i > 2 && i < 6) {
                    state = LonerSymbolStates.SCATTER_TRANSFORMED;
                }
            }
            const symbol:ISymbol = SymbolUtils.getSymbol(true, symbols[i], state, {
                reelID : this._reelId,
                position : i,
                targetPosition : i
            })
            this._reelSymbols.push(symbol);

            if(symbol.symbolName == SYMBOL_NAMES.wild2X2) {
                this._bigSymbolOnReel = SYMBOL_NAMES.wild2X2;
                this._addBelowCounter = 1;
            }
            if(symbol.symbolName == SYMBOL_NAMES.wild3X3) {
                this._bigSymbolOnReel = SYMBOL_NAMES.wild3X3;
                this._addBelowCounter = 2;
            }

            if(this._addBelowCounter > 0 && symbol.symbolName !== SYMBOL_NAMES.wild2X2
                && symbol.symbolName !== SYMBOL_NAMES.wild3X3) {
                this._addBelowCounter--;
            }
            this._reelSymbolsContainer.addChild(<LonerSymbol>symbol);
        }

        this.updateSymbolsPosition();

        if(this._doInit == true) {
            this._doInit = false;
            this.init();
        }
    }

    private getNormalStartOvershootTween():gsap.core.Tween {
        return gsap.to(this.animatedStepData, {
            duration : LonerStatics.NORMAL_START_SPIN_BOUNCE_DURATION_FACTOR,
            y : 0 - LonerStatics.NORMAL_START_SPIN_BOUNCE_PIXELS,
            ease : "power1.out",
            onUpdate : () => this.updateSymbolsPosition()
        });
    }

    private getNormalSpinStartOvershootBackTween(symbolIndex:number):gsap.core.Tween {
        return gsap.to(this.animatedStepDataStart[symbolIndex], {
            duration : LonerStatics.NORMAL_START_SPIN_ACCELERATOR_FACTOR,
            y : 0 + (LonerStatics.spinSymbolHeight * symbolIndex) + LonerStatics.spinSymbolHeight,
            ease : "power1.in",
            onUpdate : () => this.updateStartStopSymbolsPosition()
        });
    }

    private getFastSpinStartOvershootBackTween(symbolIndex:number):gsap.core.Tween {
        return gsap.to(this.animatedStepDataStart[symbolIndex], {
            duration : LonerStatics.FAST_START_SPIN_ACCELERATOR_FACTOR,
            y : 0 + (LonerStatics.spinSymbolHeight * symbolIndex) + LonerStatics.spinSymbolHeight,
            ease : "power1.in",
            onUpdate : () => this.updateStartStopSymbolsPosition()
        });
    }

    //Moving 2 symbols down and adjusting back to no spin symbols positioning
    private getNormalSpinStopTween(symbolIndex:number):gsap.core.Tween {
        let bouncePixelAmount:number = LonerStatics.STOP_SPIN_BOUNCE_PIXEL_AMOUNT;
        if(this._reelId == 4) {
            bouncePixelAmount = 2;
        }
        return gsap.to(this.animatedStepDataStart[symbolIndex], {
            duration : LonerStatics.STOP_SPIN_STOP_ADJUST_DURATION,
            y : (LonerStatics.symbolHeight + (LonerStatics.symbolHeight * symbolIndex) + LonerStatics.symbolHeight) + bouncePixelAmount,
            ease : "power1.out",
            onUpdate : () => this.updateStartStopSymbolsPosition()
        });
    }

    //Moving 2 symbols down and adjusting back to no spin symbols positioning
    private getFastSpinStopTween(symbolIndex:number):gsap.core.Tween {
        let bouncePixelAmount:number = LonerStatics.STOP_SPIN_BOUNCE_FAST_PIXEL_AMOUNT;
        return gsap.to(this.animatedStepDataStart[symbolIndex], {
            duration : LonerStatics.STOP_SPIN_STOP_ADJUST_FAST_DURATION,
            y : (LonerStatics.symbolHeight + (LonerStatics.symbolHeight * symbolIndex) + LonerStatics.symbolHeight) + bouncePixelAmount,
            ease : "power1.out",
            onUpdate : () => this.updateStartStopSymbolsPosition()
        });
    }

    private getNormalSpinStopOvershootBackTween():gsap.core.Tween {
        let duration:number = LonerStatics.STOP_SPIN_BOUNCE_BACK_DURATION;

        if(this._reelId == 4) {
            duration -= 0.001;
        }

        return gsap.to(this.animatedStepData, {
            duration : duration,
            y : 0,
            ease : "power1.out",
            onUpdate : () => this.updateSymbolsPosition()
        });
    }

    private getFastSpinStopOvershootBackTween():gsap.core.Tween {
        let duration:number = LonerStatics.STOP_SPIN_BOUNCE_BACK_FAST_DURATION;

        return gsap.to(this.animatedStepData, {
            duration : duration,
            y : 0,
            ease : "power1.out",
            onUpdate : () => this.updateSymbolsPosition()
        });
    }

    private updateStartStopSymbolsPosition() {
        for(let i:number = 0; i < this._reelSymbols.length; i++) {
            this._reelSymbols[i].position.y = this.animatedStepDataStart[i].y;
        }
    }

    private getFastStartOvershootTween():gsap.core.Tween {
        //TODO Tweak speed
        return gsap.to(this.animatedStepData, {
            duration : LonerStatics.stepDuration * LonerStatics.FAST_START_SPIN_BOUNCE_DURATION_FACTOR,
            y : 0 - (LonerStatics.stepSize * LonerStatics.FAST_START_SPIN_BOUNCE_FACTOR),
            ease : "power1.out",
            onStart : () => {
                for(let i:number = 0; i < this._reelSymbols.length; i++) {
                    if(this._reelSymbols[i].symbolName == "S") {
                        (<LonerScatterSymbol>this._reelSymbols[i]).getNewStateAnimation(LonerSymbolStates.SPIN_OUT, LonerStatics.FAST_BLUR_DURATION_SPIN_START, LonerStatics.FAST_BLUR_DELAY_SPIN_START);
                    } else {
                        (<LonerSymbol>this._reelSymbols[i]).getNewStateAnimation(LonerSymbolStates.SPIN_OUT, LonerStatics.FAST_BLUR_DURATION_SPIN_START, LonerStatics.FAST_BLUR_DELAY_SPIN_START);
                    }
                }
            },
            onUpdate : () => this.updateSymbolsPosition()
        });
    }

    private getNormalSpinTween():gsap.core.Tween {
        return gsap.to(this.animatedStepData, {
            duration : LonerStatics.spinStepDurationNormal,
            y : LonerStatics.spinStepSize,
            ease : "none",
            onUpdate : () => this.updateSymbolsPosition(LonerStatics.spinSymbolHeight),
            onComplete : () => this.onSpinAnimationComplete()
        })
    }

    private getFastSpinTween():gsap.core.Tween {
        return gsap.to(this.animatedStepData, {
            duration : LonerStatics.spinStepDurationFast,
            y : LonerStatics.stepSize,
            ease : "none",
            onUpdate : () => this.updateSymbolsPosition(),
            onComplete : () => this.onSpinAnimationComplete()
        })
    }

    private initTLs():void {
        this._spinTL = gsap.timeline({
            paused : true,
            repeat : -1
        })
        this._spinTL.add(this.getNormalSpinTween());

        this._spinTLFast = gsap.timeline({
            paused : true,
            repeat : -1
        })
        this._spinTLFast.add(this.getFastSpinTween());

        this._masterSpinTimeline = gsap.timeline({paused : true});
        this._masterSpinTimeline.add(this._spinTL.restart());

        this._masterSpinTimelineFast = gsap.timeline({paused : true});
        this._masterSpinTimelineFast.add(this._spinTLFast.restart());
    }

    private createActivateDarkReelTL():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline({paused : true});
        tl.add(gsap.to(this._darkOverlay, {duration : 0.2, alpha : 0.70}));
        return tl;
    }

    private createDeActivateDarkReelTL():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline({paused : true});
        tl.add(gsap.to(this._darkOverlay, {duration : 0.2, alpha : 0}));
        return tl;
    }

    private startSpin():void {
        this._spinning = true;
        Loner.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? this._masterSpinTimelineFast.restart() : this._masterSpinTimeline.restart();
    }

    private removeSym(symbol:LonerSymbol):void {
        if(symbol.symbolName == SYMBOL_NAMES.S) {
            (<LonerScatterSymbol>symbol).reset();
        } else if(symbol.symbolName == SYMBOL_NAMES.wild2X2) {
            this._bigSymbolOnReel = "";
            (<LonerBigWildPresentationSymbol2x2>symbol).reset();
        } else if(symbol.symbolName == SYMBOL_NAMES.wild3X3) {
            this._bigSymbolOnReel = "";
            (<LonerBigWildPresentationSymbol3x3>symbol).reset();
        } else {
            symbol.reset();
        }

        this._reelSymbolsContainer.removeChild(symbol);
    }

    private addSymbol(symbolState:number = LonerSymbolStates.SPIN_IN) {
        const newSymbolPosition:number = this._reelSymbols[0].reelPosition - 1;
        let symbolName:string;
        let targetPosition:number = 0;

        if(this._bigSymbolCounter > 0) {
            this._bigSymbolCounter--;
            symbolName = this.getRandomSymbolInReelSet(false);
            if(this._addBelowCounter > 0) {
                if(symbolName !== SYMBOL_NAMES.S) {
                    symbolName += "_HIDDEN";
                    this._addBelowCounter--;
                }
            }
        } else if(this._stopSymbolNames.length > 0 || this._stopping == true) {
            //Get next stop sym
            symbolName = this._stopSymbolNames.pop()!;
            targetPosition = this._stopSymbolNames.length;
        } else {
            if(this._tryToStop) {
                //Waiting for all reels to finish stacked syms. Getting a random, no-stacked sym.
                symbolName = this.getRandomSymbolInReelSet(false);
                if(Loner.allReelView.mainReelModels[this._reelId].state == SimpleReelModelState.SPIN) {
                    this._stateChangeCallback(SimpleReelModelState.READY_TO_STOP);
                }
            } else {
                //Spinning. Get a random whatever sym.
                symbolName = this.getRandomSymbolInReelSet(true);
                if(symbolName == SYMBOL_NAMES.wild2X2) {
                    this._addBelowCounter = 1;
                    this._bigSymbolCounter = 2;
                    //No Wild 3x until the Wild 2x has moved a couple of steps
                    Loner.allReelView.mainReelModels[this._reelId + 1].reelView.bigSymbolCounter = 2;
                } else if(symbolName == SYMBOL_NAMES.wild3X3) {
                    this._addBelowCounter = 2;
                    this._bigSymbolCounter = 4;
                    //No Wild 2x until the Wild 3x has moved a couple of steps
                    Loner.allReelView.mainReelModels[this._reelId - 1].reelView.bigSymbolCounter = 4;
                }
            }
        }

        this._addSymbol = SymbolUtils.getSymbol(this._stopping, symbolName, symbolState, {
            reelID : this._reelId,
            position : newSymbolPosition,
            targetPosition : targetPosition
        });

        if(this._addSymbol.symbolName == SYMBOL_NAMES.wild2X2) {
            this._bigSymbolOnReel = SYMBOL_NAMES.wild2X2;
            this._addBelowCounter = 1;
        }
        if(this._addSymbol.symbolName == SYMBOL_NAMES.wild3X3) {
            this._bigSymbolOnReel = SYMBOL_NAMES.wild3X3;
            this._addBelowCounter = 2;
        }

        if(this._addBelowCounter > 0 && this._addSymbol.symbolName !== SYMBOL_NAMES.wild2X2
            && this._addSymbol.symbolName !== SYMBOL_NAMES.wild3X3) {
            this._addBelowCounter--;
        }
        this._reelSymbolsContainer.addChild(<LonerSymbol>this._addSymbol);

        this._reelSymbols.unshift(this._addSymbol);
    }

    //All symbols on mainReels have moved one symbol in height
    private onSpinAnimationComplete() {
        this.removeSym(<LonerSymbol>this._reelSymbols.pop());

        for(let i:number = 0; i < this._reelSymbols.length; i++) {
            const symbol:ISymbol = this._reelSymbols[i];
            (<LonerSymbol>symbol).reelPosition += 1;
        }

        this.animatedStepData.y = 0;

        if(this._stopping == true) {
            this._stopSteps--;
            if(this._stopSteps == 0) {
                if(this._masterSpinTimeline.isActive()) {
                    this._masterSpinTimeline.pause();
                } else {
                    this._masterSpinTimelineFast.pause();
                }
                this._stopping = false;
                this._tryToStop = false;

                //Do stop adjust and  bounce
                Loner.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? this._stopAdjustFastTL.restart() : this._stopAdjustTL.restart();

            } else {
                this.addSymbol();
                this.updateSymbolsPosition(LonerStatics.spinSymbolHeight);
            }
        } else {
            this.addSymbol();
            this.updateSymbolsPosition(LonerStatics.spinSymbolHeight);
        }
    }

    private updateSymbolsPosition(stepSize:number = LonerStatics.symbolHeight) {
        for(let i:number = 0; i < this._reelSymbols.length; i++) {
            let basePos:number = stepSize * this._reelSymbols[i].reelPosition;
            this._reelSymbols[i].position.y = basePos + this.animatedStepData.y;
        }
    }

    private ifReelContainsBigWilds(reelId:number):boolean {
        const reelSymbols:ISymbol[] = Loner.allReelView.mainReelModels[reelId].reelView._reelSymbols;
        for(let i:number = 0; i < reelSymbols.length; i++) {
            if(reelSymbols[i].symbolName == SYMBOL_NAMES.wild2X2 || reelSymbols[i].symbolName == SYMBOL_NAMES.wild3X3) {
                return true;
            }
        }
        return false;
    }

    private getRandomSymbolInReelSet(getBigWild:boolean):string {
        if(getBigWild == true) {
            if(this.ifReelContainsBigWilds(this._reelId) == false) {
                if(this._reelId == 2) {
                    return LonerStatics.SYMBOLS.concat(SYMBOL_NAMES.wild2X2)[MathHelper.randomIntInRange(0, (LonerStatics.SYMBOLS.concat(SYMBOL_NAMES.wild2X2).length - 1))];
                } else if(this._reelId == 3) {
                    return LonerStatics.SYMBOLS.concat(SYMBOL_NAMES.wild3X3)[MathHelper.randomIntInRange(0, (LonerStatics.SYMBOLS.concat(SYMBOL_NAMES.wild3X3).length - 1))];
                }
            }
        }

        //Add possible scatter if correct reel
        if(this._reelId == 0 || this._reelId == 2 || this._reelId == 4) {
            return LonerStatics.SYMBOLS.concat(SYMBOL_NAMES.S)[MathHelper.randomIntInRange(0, (LonerStatics.SYMBOLS.concat(SYMBOL_NAMES.S).length - 1))];
        }
        return LonerStatics.SYMBOLS[MathHelper.randomIntInRange(0, (LonerStatics.SYMBOLS.length - 1))];
    }

    get symbols():ISymbol[] {
        return this._reelSymbols;
    }

    set bigSymbolCounter(value:number) {
        this._bigSymbolCounter = value;
    }
}